import React, { useMemo } from 'react';
import styled from 'styled-components';
import { TextStyleBody, TextStyleSubHeader } from '@style/Shared';
import { Colors } from '@client/style/Variables';
import { useFavourites } from '../Favourites/FavouriteStore';
import FilteredList from '../ListModules/FilteredList/FilteredList';
import FilterStore from '../ListModules/FilterStore';
import { DateUtils } from '@client/core/utils/DateUtils';

const Container = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	padding-top: 100px;
	position: relative;
	align-items: center;
	padding: 0px var(--gridMargin);

	&:has(+ .BreakerModule) {
		border-bottom: var(--breaker-total) solid transparent;
	}
`;

const Header = styled.h2`
	${TextStyleSubHeader}
	color: ${Colors.darkText};
	text-align: center;
	margin-top: 20px;
`;

const NoFavourites = styled.span`
	${TextStyleBody}
	color: ${Colors.darkText};
	text-align: center;
	width: 50%;
`;

export default function MyPageFavourites({ artists, ...props }: { artists: { id: string }[] }) {
	const favourites = useFavourites();

	const favouriteArtists = useMemo(() => {
		const favouritesIds = favourites.map(f => f.id);
		return artists.filter(a => favouritesIds.includes(a.id));
	}, [favourites, artists]);

	const locationFilter = FilterStore.use(state => state.selectedLocationFilters);
	const tagFilter = FilterStore.use(state => state.selectedTagFilters);
	const dateFilter = FilterStore.use(state => state.selectedDateFilters);

	const filteredFavourites = useMemo(() => {
		const result: any[] = favouriteArtists.filter((e: any) => {
			if (locationFilter.length > 0 && !locationFilter.includes(e.location?.id)) return false;
			if (tagFilter.length > 0 && !tagFilter.includes(e.tags?.id)) return false;
			if (dateFilter) {
				if (e.startTime === null) return false;
				if (!DateUtils.isOnSameSmukDay(new Date(e.startTime), new Date(dateFilter))) return false;
			}

			return true;
		});

		return result.sort((a, b) => {
			return a.title.localeCompare(b.title);
		});
	}, [favouriteArtists, locationFilter, tagFilter, dateFilter]);

	return (
		<Container {...props}>
			<Header>Her er dine favoritter</Header>
			{favouriteArtists.length === 0 && (
				<NoFavourites>
					Øv, du har ingen favoritter. Hvis du markerer en kunstner som favorit vil artisten blive fremhævet i f.eks.
					spilleplanen.
				</NoFavourites>
			)}

			{favouriteArtists.length > 0 && (
				<FilteredList
					contents={filteredFavourites}
					data={{
						useLocationFilter: false,
						useTagFilter: false,
						useDateFilter: true,
						backgroundColorLink: null,
						detailedThumbnail: false,
						detailedThumbnailWithTimestamp: false,
						noMatchesText: '',
					}}
				/>
			)}
		</Container>
	);
}
